import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchableFacilitySelect from '../components/SearchableFacilitySelect';
import { equipmentService, vendorService, facilityService } from '../services/dataService';
import { useAuth } from '../hooks/useAuth';
import { hasPermission } from '../utils/permissions';
import styles from './assets/css/EquipmentPage.module.css';

const initialCreateForm = {
  vendor_id: '',
  asset_id: '',
  serial_number: '',
  name: '',
  category: '',
  manufacturer: '',
  model: '',
  model_version: '',
  software_version: '',
  facility_id: '',
  department: '',
  location: '',
  country_of_origin: 'KE',
  worklist_ingestion_method: 'dicom_mwl',
  transmission_method: 'dicom_mpps',
  dicom_aet: '',
  dicom_host: '',
  dicom_port: '',
  lifecycle_state: 'active',
  operational_status: 'operational',
  maintenance_notes: '',
};

const initialEditForm = {
  name: '',
  category: '',
  manufacturer: '',
  model: '',
  model_version: '',
  software_version: '',
  department: '',
  location: '',
  worklist_ingestion_method: 'dicom_mwl',
  transmission_method: 'dicom_mpps',
  dicom_aet: '',
  dicom_host: '',
  dicom_port: '',
  lifecycle_state: 'active',
  operational_status: 'operational',
  maintenance_notes: '',
};

const EquipmentPage = () => {
  const { user } = useAuth();
  const canPublish = hasPermission(user, 'equipment', 'write');
  const [items, setItems] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [modalityOptions, setModalityOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [operational, setOperational] = useState('');
  const [linkedFilter, setLinkedFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [republishing, setRepublishing] = useState(false);
  const [republishForce, setRepublishForce] = useState(false);
  const [republishNotice, setRepublishNotice] = useState('');
  const [republishError, setRepublishError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [exporting, setExporting] = useState(false);
  const [pingRequests, setPingRequests] = useState([]);
  const [pingLoading, setPingLoading] = useState(false);
  const [pingError, setPingError] = useState('');
  const [pingNotice, setPingNotice] = useState('');
  const [pingActionId, setPingActionId] = useState('');
  const [pingEquipmentSelection, setPingEquipmentSelection] = useState({});
  const [linkedPingEquipmentIds, setLinkedPingEquipmentIds] = useState([]);
  const [pingFacilityFilter, setPingFacilityFilter] = useState('');
  const [pingAeTitleFilter, setPingAeTitleFilter] = useState('');
  const [pingPage, setPingPage] = useState(1);
  const [pingPageSize, setPingPageSize] = useState(10);
  const [pingConfirm, setPingConfirm] = useState(null); // { requestId, pingAeTitle, equipmentAeTitle, equipmentId }

  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState(initialEditForm);
  const [saving, setSaving] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const linkedPingEquipmentIdSet = useMemo(
    () => new Set(linkedPingEquipmentIds),
    [linkedPingEquipmentIds],
  );
  const equipmentStats = useMemo(() => {
    const operationalStatusCounts = equipmentOptions.reduce((acc, item) => {
      const key = item.operational_status || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return {
      totalMachines: equipmentOptions.length,
      linkedPingMachines: linkedPingEquipmentIdSet.size,
      operationalStatusCounts,
    };
  }, [equipmentOptions, linkedPingEquipmentIdSet]);
  const unlinkedEquipmentOptions = useMemo(
    () => equipmentOptions.filter((item) => !linkedPingEquipmentIdSet.has(item.id)),
    [equipmentOptions, linkedPingEquipmentIdSet],
  );
  const facilityById = useMemo(
    () => facilities.reduce((acc, facilityItem) => {
      acc[facilityItem.id] = facilityItem;
      return acc;
    }, {}),
    [facilities],
  );

  const getEquipmentFilters = useCallback(() => ({
    facility_name: facilityName || undefined,
    lifecycle_state: lifecycle || undefined,
    operational_status: operational || undefined,
    search: search || undefined,
    linked: linkedFilter || undefined,
  }), [facilityName, lifecycle, operational, search, linkedFilter]);

  const getFacilityName = useCallback((facilityId) => (
    facilityById[facilityId]?.name || 'Unknown Facility'
  ), [facilityById]);

  const getPingEquipmentLabel = useCallback((equipment) => (
    [
      equipment.name || equipment.asset_id || 'Unnamed equipment',
      equipment.asset_id,
      equipment.dicom_aet ? `AET: ${equipment.dicom_aet}` : null,
      equipment.category || 'N/A',
      getFacilityName(equipment.facility_id),
    ].filter(Boolean).join(' | ')
  ), [getFacilityName]);

  const getPingEquipmentSearchText = useCallback((equipment) => (
    [
      equipment.name,
      equipment.asset_id,
      equipment.serial_number,
      equipment.dicom_aet,
      equipment.category,
      equipment.facility_id,
      getFacilityName(equipment.facility_id),
    ].filter(Boolean).join(' ')
  ), [getFacilityName]);

  const equipmentById = useMemo(
    () => equipmentOptions.reduce((acc, equipment) => {
      acc[equipment.id] = equipment;
      return acc;
    }, {}),
    [equipmentOptions],
  );

  const getPingRequestEquipment = useCallback((request) => {
    const selectedId = pingEquipmentSelection[request.id] || request.equipment_id;
    return selectedId ? equipmentById[selectedId] : null;
  }, [equipmentById, pingEquipmentSelection]);

  const filteredPingRequests = useMemo(() => {
    const normalizedAeTitle = pingAeTitleFilter.trim().toLowerCase();
    return pingRequests.filter((request) => {
      const equipment = getPingRequestEquipment(request);
      const facilityId = equipment?.facility_id || request.facility_id || '';
      const equipmentAeTitle = equipment?.dicom_aet || '';
      return (
        (!pingFacilityFilter || facilityId === pingFacilityFilter)
        && (!normalizedAeTitle || equipmentAeTitle.toLowerCase().includes(normalizedAeTitle))
      );
    });
  }, [getPingRequestEquipment, pingAeTitleFilter, pingFacilityFilter, pingRequests]);

  const pingTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredPingRequests.length / pingPageSize)),
    [filteredPingRequests.length, pingPageSize],
  );
  const pagedPingRequests = useMemo(() => {
    const start = (pingPage - 1) * pingPageSize;
    return filteredPingRequests.slice(start, start + pingPageSize);
  }, [filteredPingRequests, pingPage, pingPageSize]);

  const sortedItems = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const getValue = (item) => {
      if (sortConfig.key === 'facility') return getFacilityName(item.facility_id);
      if (sortConfig.key === 'linked_ping_mwl') return item.linked_ping_mwl || '';
      return item[sortConfig.key] || '';
    };
    return [...items].sort((a, b) => String(getValue(a)).localeCompare(String(getValue(b))) * direction);
  }, [items, sortConfig, getFacilityName]);

  const loadVendors = useCallback(async () => {
    try {
      const data = await vendorService.getAll({ page: 1, page_size: 100 });
      const list = data.items || [];
      setVendors(list);
      if (!createForm.vendor_id && list.length > 0) {
        setCreateForm((v) => ({ ...v, vendor_id: list[0].id }));
      }
    } catch (_) {
      // Keep page usable even if vendor list fails.
    }
  }, [createForm.vendor_id]);

  const loadFormDependencies = useCallback(async () => {
    try {
      const [
        facilityData,
        modalityData,
        departmentData,
        locationData,
      ] = await Promise.all([
        facilityService.getAllPages(),
        vendorService.getDropdownConfig({ category: 'modality', status: 'active' }),
        vendorService.getDropdownConfig({ category: 'hosp_department', status: 'active' }),
        vendorService.getDropdownConfig({ category: 'hosp_location', status: 'active' }),
      ]);

      const facilityItems = facilityData || [];
      const modalityItems = modalityData.items || [];
      const departmentItems = departmentData.items || [];
      const locationItems = locationData.items || [];

      setFacilities(facilityItems);
      setModalityOptions(modalityItems);
      setDepartmentOptions(departmentItems);
      setLocationOptions(locationItems);

      setCreateForm((prev) => ({
        ...prev,
        facility_id: prev.facility_id || facilityItems[0]?.id || '',
      }));
    } catch (_) {
      // Create form can still work with manually entered fields.
    }
  }, []);

  const loadEquipment = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentService.getAll({
        page: targetPage,
        page_size: pageSize,
        ...getEquipmentFilters(),
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err?.detail || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, getEquipmentFilters]);

  const loadEquipmentOptions = useCallback(async () => {
    try {
      const pageSize = 100;
      let currentPage = 1;
      let total = 0;
      const allItems = [];

      do {
        const data = await equipmentService.getAll({ page: currentPage, page_size: pageSize });
        const itemsPage = data.items || [];
        total = data.total || 0;
        allItems.push(...itemsPage);
        currentPage += 1;
      } while (allItems.length < total);

      setEquipmentOptions(allItems);
    } catch (_) {
      setEquipmentOptions([]);
    }
  }, []);

  const loadPingRequests = useCallback(async () => {
    setPingLoading(true);
    setPingError('');
    try {
      const pendingItems = [];
      let currentPage = 1;
      let totalItems = 0;

      do {
        const data = await equipmentService.getPendingPingRequests({ page: currentPage, page_size: 500 });
        const pageItems = data.items || [];
        totalItems = data.total || 0;
        pendingItems.push(...pageItems);
        currentPage += 1;
        if (pageItems.length === 0) break;
      } while (pendingItems.length < totalItems);

      setPingRequests(pendingItems);
      setPingEquipmentSelection((prev) => {
        const next = { ...prev };
        pendingItems.forEach((request) => {
          if (!next[request.id] && request.equipment_id) {
            next[request.id] = request.equipment_id;
          }
        });
        return next;
      });
    } catch (err) {
      setPingError(err?.detail || 'Failed to load ping requests');
    } finally {
      setPingLoading(false);
    }
  }, []);

  const loadLinkedPingEquipmentIds = useCallback(async () => {
    try {
      const ids = await equipmentService.getLinkedPingEquipmentIds();
      setLinkedPingEquipmentIds(ids || []);
    } catch (_) {
      setLinkedPingEquipmentIds([]);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    loadFormDependencies();
  }, [loadFormDependencies]);

  const handleCreateCategoryChange = (value) => {
    const selected = modalityOptions.find((option) => option.code === value);
    setCreateForm((prev) => ({
      ...prev,
      category: value,
      name: selected?.name || '',
    }));
  };

  const handleEditCategoryChange = (value) => {
    const selected = modalityOptions.find((option) => option.code === value);
    setEditForm((prev) => ({
      ...prev,
      category: value,
      name: selected?.name || '',
    }));
  };

  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  useEffect(() => {
    loadEquipmentOptions();
  }, [loadEquipmentOptions]);

  useEffect(() => {
    loadPingRequests();
    loadLinkedPingEquipmentIds();
    const timer = window.setInterval(() => {
      loadPingRequests();
      loadLinkedPingEquipmentIds();
    }, 5000);

    return () => window.clearInterval(timer);
  }, [loadPingRequests, loadLinkedPingEquipmentIds]);

  useEffect(() => {
    setPingPage(1);
  }, [pingFacilityFilter, pingAeTitleFilter, pingPageSize]);

  useEffect(() => {
    if (pingPage > pingTotalPages) setPingPage(pingTotalPages);
  }, [pingPage, pingTotalPages]);

  const toggleSelected = (id) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const toggleAllSelected = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((item) => item.id)));

  const handleBatchRepublish = async () => {
    const equipmentIds = [...selectedIds];
    const scope = equipmentIds.length
      ? `${equipmentIds.length} selected machine(s)`
      : 'every linked machine';
    const mode = republishForce ? 'including ones already registered' : 'only those missing or out of date in Orthanc';
    if (!window.confirm(`Republish ${scope} to Orthanc, ${mode}?`)) return;

    setRepublishing(true);
    setRepublishNotice('');
    setRepublishError('');
    try {
      const result = await equipmentService.publishToOrthancBatch({ equipmentIds, onlyMissing: !republishForce });
      const byReason = result.published.reduce((acc, item) => ({ ...acc, [item.reason]: (acc[item.reason] || 0) + 1 }), {});
      const reasons = Object.entries(byReason).map(([reason, count]) => `${count} ${reason}`).join(', ');
      setRepublishNotice(
        `Checked ${result.checked}: published ${result.published.length}${reasons ? ` (${reasons})` : ''}, `
        + `${result.already_registered} already registered.`,
      );
      const problems = [
        ...result.skipped.map((item) => `${item.name || item.asset_id} skipped: ${item.reason}`),
        ...result.failed.map((item) => `${item.name || item.asset_id} (${item.ae_title}) failed: ${item.reason}`),
      ];
      if (problems.length) setRepublishError(problems.join(' | '));
      setSelectedIds(new Set());
    } catch (err) {
      setRepublishError(err?.detail || 'Batch republish to Orthanc failed');
    } finally {
      setRepublishing(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadEquipment(1);
  };

  const changeSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const escapeCsv = (value) => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsv = (filename, rows, columns) => {
    const csv = [
      columns.map(([, label]) => escapeCsv(label)).join(','),
      ...rows.map((row) => columns.map(([key]) => escapeCsv(typeof key === 'function' ? key(row) : row[key])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportEquipment = async () => {
    setExporting(true);
    setError('');
    try {
      const allItems = [];
      let currentPage = 1;
      let totalItems = 0;
      do {
        const data = await equipmentService.getAll({
          page: currentPage,
          page_size: 100,
          ...getEquipmentFilters(),
        });
        const pageItems = data.items || [];
        totalItems = data.total || 0;
        allItems.push(...pageItems);
        currentPage += 1;
      } while (allItems.length < totalItems);

      downloadCsv(
        `equipment-${new Date().toISOString().slice(0, 10)}.csv`,
        allItems,
        [
          [(row) => allItems.indexOf(row) + 1, '#'],
          ['name', 'Name'],
          ['dicom_aet', 'DICOM AET'],
          [(row) => getFacilityName(row.facility_id), 'Facility'],
          ['facility_id', 'FID'],
          ['category', 'Category'],
          ['linked_ping_mwl', 'Linked MWL'],
          ['lifecycle_state', 'Lifecycle'],
          ['operational_status', 'Operational Status'],
        ],
      );
    } catch (err) {
      setError(err?.detail || 'Failed to export equipment');
    } finally {
      setExporting(false);
    }
  };

  const buildCreatePayload = () => {
    const payload = {
      ...createForm,
      dicom_port: createForm.dicom_port ? Number(createForm.dicom_port) : null,
      country_of_origin: (createForm.country_of_origin || '').toUpperCase(),
    };

    [
      'asset_id',
      'serial_number',
      'manufacturer',
      'model',
      'model_version',
      'software_version',
      'department',
      'location',
      'dicom_aet',
      'dicom_host',
      'maintenance_notes',
    ].forEach((field) => {
      if (payload[field] === '') {
        delete payload[field];
      }
    });

    if (!payload.dicom_port) {
      delete payload.dicom_port;
    }

    return payload;
  };

  const buildEditPayload = () => {
    const payload = {
      ...editForm,
      dicom_port: editForm.dicom_port ? Number(editForm.dicom_port) : null,
    };

    [
      'manufacturer',
      'model',
      'model_version',
      'software_version',
      'department',
      'location',
      'dicom_aet',
      'dicom_host',
      'maintenance_notes',
    ].forEach((field) => {
      if (payload[field] === '') {
        delete payload[field];
      }
    });

    if (!payload.dicom_port) {
      delete payload.dicom_port;
    }

    return payload;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await equipmentService.create(buildCreatePayload());
      setCreateForm((v) => ({
        ...initialCreateForm,
        vendor_id: v.vendor_id || '',
        facility_id: v.facility_id || '',
      }));
      setShowCreateModal(false);
      await loadEquipment();
      await loadEquipmentOptions();
    } catch (err) {
      setError(err?.detail || 'Failed to create equipment');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      name: item.name || '',
      category: item.category || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      model_version: item.model_version || '',
      software_version: item.software_version || '',
      department: item.department || '',
      location: item.location || '',
      worklist_ingestion_method: item.worklist_ingestion_method || 'dicom_mwl',
      transmission_method: item.transmission_method || 'dicom_mpps',
      dicom_aet: item.dicom_aet || '',
      dicom_host: item.dicom_host || '',
      dicom_port: item.dicom_port || '',
      lifecycle_state: item.lifecycle_state || 'active',
      operational_status: item.operational_status || 'operational',
      maintenance_notes: item.maintenance_notes || '',
    });
  };

  const cancelEdit = () => {
    setEditId('');
    setEditForm(initialEditForm);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    setError('');
    try {
      await equipmentService.update(editId, buildEditPayload());
      cancelEdit();
      await loadEquipment();
      await loadEquipmentOptions();
    } catch (err) {
      setError(err?.detail || 'Failed to update equipment');
    } finally {
      setSaving(false);
    }
  };

  // Open the confirmation modal instead of approving directly
  const handleApprovePing = (id) => {
    const request = pingRequests.find((r) => r.id === id);
    if (!request) return;
    const selectedEquipmentId = pingEquipmentSelection[id] || request.equipment_id || null;
    const selectedEquipment = equipmentOptions.find((e) => e.id === selectedEquipmentId);
    const pingAeTitle = request.ae_title || '';
    const systemGeneratedAeTitle = selectedEquipment?.dicom_aet || '';
    const defaultSource = pingAeTitle ? 'machine_ping' : 'system_generated';
    const defaultAeTitle = defaultSource === 'machine_ping' ? pingAeTitle : systemGeneratedAeTitle;

    setPingConfirm({
      requestId: id,
      pingAeTitle,
      systemGeneratedAeTitle,
      equipmentId: selectedEquipmentId,
      aeTitleSource: defaultSource,
      selectedAeTitle: defaultAeTitle,
    });
  };

  const handlePingAeSourceChange = (source) => {
    setPingConfirm((prev) => {
      if (!prev) return prev;
      const nextTitle = source === 'machine_ping'
        ? (prev.pingAeTitle || '')
        : (prev.systemGeneratedAeTitle || '');
      return {
        ...prev,
        aeTitleSource: source,
        selectedAeTitle: nextTitle,
      };
    });
  };

  const handlePingAeTitleEdit = (value) => {
    setPingConfirm((prev) => (prev ? { ...prev, selectedAeTitle: value } : prev));
  };

  const confirmApprovePing = async () => {
    if (!pingConfirm) return;
    const { requestId, equipmentId, aeTitleSource, selectedAeTitle } = pingConfirm;
    setPingConfirm(null);
    setPingActionId(requestId);
    setPingError('');
    setPingNotice('');
    try {
      const linked = await equipmentService.approvePingRequest(requestId, {
        approval_reason: 'Approved from Equipment queue',
        equipment_id: equipmentId,
        ae_title_source: aeTitleSource,
        selected_ae_title: selectedAeTitle,
      });
      const republished = linked?.worklists_republished || 0;
      setPingNotice(
        `Linked ${linked?.ae_title || 'machine'}${republished ? `; ${republished} open worklist(s) re-issued under this AE title` : ''}.`,
      );
      if (linked?.orthanc_warning) setPingError(`Linked, but Orthanc reported: ${linked.orthanc_warning}`);
      await loadPingRequests();
      await loadLinkedPingEquipmentIds();
    } catch (err) {
      setPingError(err?.detail || 'Failed to approve ping request');
    } finally {
      setPingActionId('');
    }
  };

  const handleRejectPing = async (id) => {
    setPingActionId(id);
    setPingError('');
    try {
      await equipmentService.rejectPingRequest(id, 'Rejected from Equipment queue');
      await loadPingRequests();
    } catch (err) {
      setPingError(err?.detail || 'Failed to reject ping request');
    } finally {
      setPingActionId('');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>Equipment Management</h1>
        <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>Add Equipment</button>
      </div>

      <div className={styles.panel}>
        <h2>Filters</h2>
        <form onSubmit={onSearch} className={styles.grid4}>
          <input placeholder="Search by name or asset id" value={search} onChange={(e) => setSearch(e.target.value)} />
          <input placeholder="Facility name" value={facilityName} onChange={(e) => setFacilityName(e.target.value)} />
          <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
            <option value="">All lifecycle states</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="maintenance">maintenance</option>
            <option value="disabled">disabled</option>
            <option value="retired">retired</option>
          </select>
          <select value={operational} onChange={(e) => setOperational(e.target.value)}>
            <option value="">All operational states</option>
            <option value="operational">operational</option>
            <option value="non_operational">non_operational</option>
            <option value="maintenance">maintenance</option>
            <option value="unknown">unknown</option>
          </select>
          <select value={linkedFilter} onChange={(e) => { setLinkedFilter(e.target.value); setPage(1); }} aria-label="Linked">
            <option value="">Linked and not linked</option>
            <option value="true">Linked</option>
            <option value="false">Not linked</option>
          </select>
          <button type="submit">Apply</button>
          <button type="button" className={styles.secondaryButton} onClick={exportEquipment} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </form>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Equipment Stats</h2>
          <span className={styles.muted}>All machines across the loaded catalog</span>
        </div>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Added Machines</span>
            <strong className={styles.statValue}>{equipmentStats.totalMachines}</strong>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Linked to Ping</span>
            <strong className={styles.statValue}>{equipmentStats.linkedPingMachines}</strong>
          </div>
          {Object.entries(equipmentStats.operationalStatusCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([status, count]) => (
              <div key={status} className={styles.statCard}>
                <span className={styles.statLabel}>{status.replace(/_/g, ' ')}</span>
                <strong className={styles.statValue}>{count}</strong>
              </div>
            ))}
        </div>
      </div>

      {showCreateModal ? (
        <div className={styles.modalOverlay}>
          <div className={styles.formModal}>
            <div className={styles.formModalHeader}>
              <h2>Create Equipment</h2>
              <button type="button" className={styles.secondaryButton} onClick={() => setShowCreateModal(false)}>Close</button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleCreate} className={styles.grid4}>
          <select required value={createForm.vendor_id} onChange={(e) => setCreateForm((v) => ({ ...v, vendor_id: e.target.value }))}>
            <option value="">Select vendor</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <input placeholder="Asset id (optional)" value={createForm.asset_id} onChange={(e) => setCreateForm((v) => ({ ...v, asset_id: e.target.value }))} />
          <input placeholder="Serial number" value={createForm.serial_number} onChange={(e) => setCreateForm((v) => ({ ...v, serial_number: e.target.value }))} />
          <input required placeholder="Name" value={createForm.name} readOnly />
          <select required value={createForm.category} onChange={(e) => handleCreateCategoryChange(e.target.value)}>
            <option value="">Select modality category</option>
            {modalityOptions.map((option) => (
              <option key={option.id} value={option.code}>{option.code} - {option.name}</option>
            ))}
          </select>
          <input placeholder="Manufacturer" value={createForm.manufacturer} onChange={(e) => setCreateForm((v) => ({ ...v, manufacturer: e.target.value }))} />
          <input placeholder="Model" value={createForm.model} onChange={(e) => setCreateForm((v) => ({ ...v, model: e.target.value }))} />
          <input placeholder="Model version" value={createForm.model_version} onChange={(e) => setCreateForm((v) => ({ ...v, model_version: e.target.value }))} />
          <input placeholder="Software version" value={createForm.software_version} onChange={(e) => setCreateForm((v) => ({ ...v, software_version: e.target.value }))} />
          <SearchableFacilitySelect
            facilities={facilities}
            value={createForm.facility_id}
            onChange={(facilityId) => setCreateForm((prev) => ({ ...prev, facility_id: facilityId }))}
            required
          />
          <select value={createForm.department} onChange={(e) => setCreateForm((v) => ({ ...v, department: e.target.value }))}>
            <option value="">Select department</option>
            {departmentOptions.map((option) => (
              <option key={option.id} value={option.name}>{option.name}</option>
            ))}
          </select>
          <select value={createForm.location} onChange={(e) => setCreateForm((v) => ({ ...v, location: e.target.value }))}>
            <option value="">Select location</option>
            {locationOptions.map((option) => (
              <option key={option.id} value={option.name}>{option.name}</option>
            ))}
          </select>
          <input placeholder="Country code" maxLength={2} value={createForm.country_of_origin} onChange={(e) => setCreateForm((v) => ({ ...v, country_of_origin: e.target.value.toUpperCase() }))} />
          <select value={createForm.worklist_ingestion_method} onChange={(e) => setCreateForm((v) => ({ ...v, worklist_ingestion_method: e.target.value }))}>
            <option value="dicom_mwl">dicom_mwl</option>
            <option value="hl7_mwl">hl7_mwl</option>
            <option value="custom_api">custom_api</option>
            <option value="file_transfer">file_transfer</option>
          </select>
          <select value={createForm.transmission_method} onChange={(e) => setCreateForm((v) => ({ ...v, transmission_method: e.target.value }))}>
            <option value="dicom_mpps">dicom_mpps</option>
            <option value="dicom_c_store">dicom_c_store</option>
            <option value="hl7_orm">hl7_orm</option>
            <option value="rest_api">rest_api</option>
            <option value="fhir_api">fhir_api</option>
          </select>
          <input placeholder="DICOM AET (auto-generated)" value={createForm.dicom_aet} readOnly />
          <input placeholder="DICOM host" value={createForm.dicom_host} onChange={(e) => setCreateForm((v) => ({ ...v, dicom_host: e.target.value }))} />
          <input type="number" placeholder="DICOM port" value={createForm.dicom_port} onChange={(e) => setCreateForm((v) => ({ ...v, dicom_port: e.target.value }))} />
          <select value={createForm.lifecycle_state} onChange={(e) => setCreateForm((v) => ({ ...v, lifecycle_state: e.target.value }))}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="maintenance">maintenance</option>
            <option value="disabled">disabled</option>
            <option value="retired">retired</option>
          </select>
          <select value={createForm.operational_status} onChange={(e) => setCreateForm((v) => ({ ...v, operational_status: e.target.value }))}>
            <option value="operational">operational</option>
            <option value="non_operational">non_operational</option>
            <option value="maintenance">maintenance</option>
            <option value="unknown">unknown</option>
          </select>
          <textarea className={styles.colSpan4} placeholder="Maintenance notes" value={createForm.maintenance_notes} onChange={(e) => setCreateForm((v) => ({ ...v, maintenance_notes: e.target.value }))} />
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Equipment'}</button>
                <button type="button" className={styles.secondaryButton} onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {!showCreateModal && !editId && error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Equipment List</h2>
          {canPublish ? (
            <div className={styles.republishBar}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={republishForce} onChange={(e) => setRepublishForce(e.target.checked)} />
                Include already registered
              </label>
              <button type="button" onClick={handleBatchRepublish} disabled={republishing}>
                {republishing
                  ? 'Republishing...'
                  : selectedIds.size
                    ? `Republish selected to Orthanc (${selectedIds.size})`
                    : 'Republish linked to Orthanc'}
              </button>
              {selectedIds.size ? (
                <button type="button" className={styles.secondaryButton} onClick={() => setSelectedIds(new Set())} disabled={republishing}>
                  Clear selection
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {republishNotice ? <div className={styles.success}>{republishNotice}</div> : null}
        {republishError ? <div className={styles.error}>{republishError}</div> : null}
        {loading ? <p>Loading equipment...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {canPublish ? (
                  <th className={styles.selectCell}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAllSelected} disabled={!items.length} aria-label="Select all equipment on this page" />
                  </th>
                ) : null}
                <th>#</th>
                <th className={styles.sortable} onClick={() => changeSort('name')}>Name</th>
                <th className={styles.sortable} onClick={() => changeSort('dicom_aet')}>DICOM AET</th>
                <th className={styles.sortable} onClick={() => changeSort('facility')}>Facility</th>
                <th className={styles.sortable} onClick={() => changeSort('category')}>Category</th>
                <th className={styles.sortable} onClick={() => changeSort('linked_ping_mwl')}>Linked MWL (Ping)</th>
                <th className={styles.sortable} onClick={() => changeSort('lifecycle_state')}>Lifecycle</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it, index) => (
                <tr key={it.id}>
                  {canPublish ? (
                    <td className={styles.selectCell}>
                      <input type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleSelected(it.id)} aria-label={`Select ${it.name}`} />
                    </td>
                  ) : null}
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>{it.name}</td>
                  <td>{it.dicom_aet || '-'}</td>
                  <td>
                    <div className={styles.stack}>
                      <strong>{getFacilityName(it.facility_id)}</strong>
                      <span className={styles.muted}>{it.facility_id}</span>
                    </div>
                  </td>
                  <td>{it.category}</td>
                  <td>{it.linked_ping_mwl || 'NOT LINKED'}</td>
                  <td>{it.lifecycle_state}</td>
                  <td className={styles.actions}>
                    <Link className={styles.actionLink} to={`/equipment/${it.id}`}>View</Link>
                    <button onClick={() => startEdit(it)}>Edit</button>
                    {/* <button className={styles.danger} onClick={() => handleDelete(it.id)} disabled>Delete</button> */}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={canPublish ? 9 : 8}>No equipment found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Ping Requests for Approval</h2>
          <span className={styles.muted}>Auto-refreshes every 5 seconds</span>
        </div>
        {pingLoading ? <p>Loading ping approvals...</p> : null}
        {pingNotice ? <div className={styles.success}>{pingNotice}</div> : null}
        {pingError ? <div className={styles.error}>{pingError}</div> : null}
        <div className={styles.pingFilters}>
          <label>
            <span>Facility</span>
            <select value={pingFacilityFilter} onChange={(event) => setPingFacilityFilter(event.target.value)}>
              <option value="">All facilities</option>
              {facilities.map((facility) => (
                <option key={facility.id} value={facility.id}>{facility.name || facility.id}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Equipment AE Title</span>
            <input
              type="search"
              value={pingAeTitleFilter}
              onChange={(event) => setPingAeTitleFilter(event.target.value)}
              placeholder="Filter by equipment AET"
            />
          </label>
          <label className={styles.rowsPerPage}>
            <span>Rows</span>
            <select value={pingPageSize} onChange={(event) => setPingPageSize(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
          <span className={styles.filterCount}>{filteredPingRequests.length} request(s)</span>
        </div>
        <div className={styles.tableWrap}>
          <table className={`${styles.table} ${styles.pingTable}`}>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Equipment</th>
                <th>AE Titles</th>
                <th>IP / Port</th>
                <th>Last Seen</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedPingRequests.map((request, index) => {
                const selectedEquipment = getPingRequestEquipment(request);
                const selectedFacilityId = selectedEquipment?.facility_id || request.facility_id;
                return (
                  <tr key={request.id}>
                    <td>{(pingPage - 1) * pingPageSize + index + 1}</td>
                    <td>{(request.request_type || 'ping').replaceAll('_', ' ')}</td>
                    <td>
                      <div className={styles.stack}>
                        <strong>{selectedEquipment?.name || request.equipment_name || 'Unmapped equipment'}</strong>
                        <span className={styles.muted}>Facility: {facilityById[selectedFacilityId]?.name || 'Unknown Facility'}</span>
                        <span className={styles.muted}>FID: {selectedFacilityId || 'N/A'}</span>
                        <SearchableFacilitySelect
                          className={styles.pingEquipmentSelect}
                          options={unlinkedEquipmentOptions}
                          value={pingEquipmentSelection[request.id] || ''}
                          onChange={(equipmentId) => setPingEquipmentSelection((prev) => ({
                            ...prev,
                            [request.id]: equipmentId,
                          }))}
                          placeholder="Search equipment"
                          emptyLabel={
                            unlinkedEquipmentOptions.length
                              ? 'Select equipment relation'
                              : 'No unlinked equipment available'
                          }
                          helperLabel="available equipment"
                          getOptionValue={(equipment) => equipment.id}
                          getOptionLabel={getPingEquipmentLabel}
                          getSearchText={getPingEquipmentSearchText}
                          disabled={!unlinkedEquipmentOptions.length}
                        />
                      </div>
                    </td>
                    <td>
                      <div className={styles.stack}>
                        <span>Ping: {request.ae_title || '-'}</span>
                        <span>Equipment: {selectedEquipment?.dicom_aet || '-'}</span>
                      </div>
                    </td>
                    <td>{request.ip_addr}:{request.port}</td>
                    <td>{new Date(request.last_seen_at).toLocaleString()}</td>
                    <td>
                      <span className={`${styles.statusTag} ${styles.pendingTag}`}>{request.approval_status}</span>
                    </td>
                    <td className={styles.actions}>
                      <button
                        onClick={() => handleApprovePing(request.id)}
                        disabled={
                          pingActionId === request.id ||
                          !(pingEquipmentSelection[request.id] || request.equipment_id)
                        }
                        title={
                          !(pingEquipmentSelection[request.id] || request.equipment_id)
                            ? 'Select equipment relation before approval'
                            : ''
                        }
                      >
                        Approve
                      </button>
                      <button
                        className={styles.danger}
                        onClick={() => handleRejectPing(request.id)}
                        disabled={pingActionId === request.id}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!pingLoading && filteredPingRequests.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    {pingRequests.length ? 'No ping requests match the selected filters.' : 'No ping requests awaiting approval.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {filteredPingRequests.length > 0 ? (
          <div className={styles.pagination}>
            <button disabled={pingPage <= 1} onClick={() => setPingPage((value) => value - 1)}>Previous</button>
            <span>Page {pingPage} / {pingTotalPages}</span>
            <button disabled={pingPage >= pingTotalPages} onClick={() => setPingPage((value) => value + 1)}>Next</button>
          </div>
        ) : null}
      </div>

      {/* ---- Ping approval confirmation modal ---- */}
      {pingConfirm ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Confirm Equipment Link</h3>
            <div className={styles.aetRow}>
              <span className={styles.aetLabel}>Ping AE Title</span>
              <span className={styles.aetValue}>{pingConfirm.pingAeTitle || '—'}</span>
            </div>
            <div className={styles.aetRow}>
              <span className={styles.aetLabel}>System Generated AET</span>
              <span className={styles.aetValue}>{pingConfirm.systemGeneratedAeTitle || '—'}</span>
              {pingConfirm.pingAeTitle && pingConfirm.systemGeneratedAeTitle ? (
                pingConfirm.pingAeTitle.toUpperCase() === pingConfirm.systemGeneratedAeTitle.toUpperCase() ? (
                  <span className={`${styles.matchBadge} ${styles.matchBadgeOk}`}>✓ Match</span>
                ) : (
                  <span className={`${styles.matchBadge} ${styles.matchBadgeWarn}`}>⚠ Mismatch</span>
                )
              ) : null}
            </div>

            <div className={styles.aeSourceBox}>
              <p className={styles.aeSourceTitle}>AE Title Source</p>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="ae-title-source"
                  checked={pingConfirm.aeTitleSource === 'machine_ping'}
                  onChange={() => handlePingAeSourceChange('machine_ping')}
                />
                Use machine ping AE title
              </label>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="ae-title-source"
                  checked={pingConfirm.aeTitleSource === 'system_generated'}
                  onChange={() => handlePingAeSourceChange('system_generated')}
                />
                Use system generated DICOM AET
              </label>
            </div>

            <div className={styles.aeEditBox}>
              <label className={styles.aeInputLabel}>Final AE Title (editable)</label>
              <input
                value={pingConfirm.selectedAeTitle || ''}
                onChange={(e) => handlePingAeTitleEdit(e.target.value)}
                placeholder="Enter final AE title"
                maxLength={64}
              />
            </div>

            {pingConfirm.pingAeTitle &&
              pingConfirm.systemGeneratedAeTitle &&
              pingConfirm.pingAeTitle.toUpperCase() !== pingConfirm.systemGeneratedAeTitle.toUpperCase() ? (
                <p className={styles.mismatchNote}>
                  The ping AE title does not match the equipment&apos;s configured DICOM AET.
                  Linking will associate this ping with the selected equipment anyway.
                  Confirm only if this is intentional.
                </p>
              ) : null}
            <div className={styles.modalActions}>
              <button type="button" onClick={() => setPingConfirm(null)} style={{ background: '#6b7280' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmApprovePing}
                disabled={!((pingConfirm.selectedAeTitle || '').trim())}
              >
                Confirm Link &amp; Approve
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.formModal}>
            <div className={styles.formModalHeader}>
              <h2>Edit Equipment</h2>
              <button type="button" className={styles.secondaryButton} onClick={cancelEdit}>Close</button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleUpdate} className={styles.grid4}>
            <input required placeholder="Name" value={editForm.name} readOnly />
            <select required value={editForm.category} onChange={(e) => handleEditCategoryChange(e.target.value)}>
              <option value="">Select modality category</option>
              {modalityOptions.map((option) => (
                <option key={option.id} value={option.code}>{option.code} - {option.name}</option>
              ))}
            </select>
            <input placeholder="Manufacturer" value={editForm.manufacturer} onChange={(e) => setEditForm((v) => ({ ...v, manufacturer: e.target.value }))} />
            <input placeholder="Model" value={editForm.model} onChange={(e) => setEditForm((v) => ({ ...v, model: e.target.value }))} />
            <input placeholder="Model version" value={editForm.model_version} onChange={(e) => setEditForm((v) => ({ ...v, model_version: e.target.value }))} />
            <input placeholder="Software version" value={editForm.software_version} onChange={(e) => setEditForm((v) => ({ ...v, software_version: e.target.value }))} />
            <select value={editForm.department} onChange={(e) => setEditForm((v) => ({ ...v, department: e.target.value }))}>
              <option value="">Select department</option>
              {departmentOptions.map((option) => (
                <option key={option.id} value={option.name}>{option.name}</option>
              ))}
            </select>
            <select value={editForm.location} onChange={(e) => setEditForm((v) => ({ ...v, location: e.target.value }))}>
              <option value="">Select location</option>
              {locationOptions.map((option) => (
                <option key={option.id} value={option.name}>{option.name}</option>
              ))}
            </select>
            <select value={editForm.worklist_ingestion_method} onChange={(e) => setEditForm((v) => ({ ...v, worklist_ingestion_method: e.target.value }))}>
              <option value="dicom_mwl">dicom_mwl</option>
              <option value="hl7_mwl">hl7_mwl</option>
              <option value="custom_api">custom_api</option>
              <option value="file_transfer">file_transfer</option>
            </select>
            <select value={editForm.transmission_method} onChange={(e) => setEditForm((v) => ({ ...v, transmission_method: e.target.value }))}>
              <option value="dicom_mpps">dicom_mpps</option>
              <option value="dicom_c_store">dicom_c_store</option>
              <option value="hl7_orm">hl7_orm</option>
              <option value="rest_api">rest_api</option>
              <option value="fhir_api">fhir_api</option>
            </select>
            <input placeholder="DICOM AET (auto-generated)" value={editForm.dicom_aet} readOnly />
            <input placeholder="DICOM host" value={editForm.dicom_host} onChange={(e) => setEditForm((v) => ({ ...v, dicom_host: e.target.value }))} />
            <input type="number" placeholder="DICOM port" value={editForm.dicom_port} onChange={(e) => setEditForm((v) => ({ ...v, dicom_port: e.target.value }))} />
            <select value={editForm.lifecycle_state} onChange={(e) => setEditForm((v) => ({ ...v, lifecycle_state: e.target.value }))}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
              <option value="maintenance">maintenance</option>
              <option value="disabled">disabled</option>
              <option value="retired">retired</option>
            </select>
            <select value={editForm.operational_status} onChange={(e) => setEditForm((v) => ({ ...v, operational_status: e.target.value }))}>
              <option value="operational">operational</option>
              <option value="non_operational">non_operational</option>
              <option value="maintenance">maintenance</option>
              <option value="unknown">unknown</option>
            </select>
            <textarea className={styles.colSpan4} placeholder="Maintenance notes" value={editForm.maintenance_notes} onChange={(e) => setEditForm((v) => ({ ...v, maintenance_notes: e.target.value }))} />
            <div className={styles.inlineActions}>
              <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Equipment'}</button>
              <button type="button" className={styles.secondaryButton} onClick={cancelEdit}>Cancel</button>
            </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EquipmentPage;
